<template>
  <div class="capture-status-display">
    <div class="status-content">
      <!-- Spinner Animation -->
      <div class="spinner-container">
        <div class="spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <div class="pulse-ring"></div>
      </div>

      <!-- Status Text -->
      <div class="status-text">
        <h3 class="status-title">Ожидание входа в приложение...</h3>
        <p class="status-instruction">
          Откройте мобильное приложение провайдера и войдите в свой аккаунт
        </p>
      </div>

      <!-- Elapsed Time -->
      <div class="elapsed-time">
        <div class="time-label">Время ожидания:</div>
        <div class="time-value">{{ formattedElapsedTime }}</div>
      </div>

      <!-- Timeout Warning -->
      <transition name="fade">
        <div v-if="showTimeoutWarning" class="timeout-warning">
          <i class="ni ni-notification-70"></i>
          <span>Осталось менее 1 минуты до истечения времени сессии</span>
        </div>
      </transition>

      <!-- Tips Section -->
      <div class="tips-section">
        <div class="tips-title">
          <i class="ni ni-bulb-61"></i>
          Советы:
        </div>
        <ul class="tips-list">
          <li>Убедитесь, что устройство подключено к той же сети Wi-Fi</li>
          <li>Проверьте, что прокси настроен правильно в настройках Wi-Fi</li>
          <li>Сертификат должен быть установлен и доверен на устройстве</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CaptureStatusDisplay',
  props: {
    startTime: {
      type: Number,
      default: () => Date.now()
    },
    timeoutMinutes: {
      type: Number,
      default: 5
    }
  },
  data() {
    return {
      elapsedSeconds: 0,
      timerInterval: null
    };
  },
  computed: {
    formattedElapsedTime() {
      const minutes = Math.floor(this.elapsedSeconds / 60);
      const seconds = this.elapsedSeconds % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },
    showTimeoutWarning() {
      const timeoutSeconds = this.timeoutMinutes * 60;
      return this.elapsedSeconds > (timeoutSeconds - 60);
    }
  },
  mounted() {
    this.startTimer();
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    startTimer() {
      this.updateElapsedTime();
      this.timerInterval = setInterval(() => {
        this.updateElapsedTime();
      }, 1000);
    },
    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },
    updateElapsedTime() {
      this.elapsedSeconds = Math.floor((Date.now() - this.startTime) / 1000);
    }
  }
};
</script>

<style scoped lang="scss">
.capture-status-display {
  text-align: center;
  padding: 2rem 1rem;

  .status-content {
    max-width: 400px;
    margin: 0 auto;
  }

  .spinner-container {
    position: relative;
    width: 100px;
    height: 100px;
    margin: 0 auto 2rem;

    .spinner {
      width: 100%;
      height: 100%;
      position: relative;

      .spinner-ring {
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        border: 3px solid transparent;
        border-top-color: #4361EE;
        animation: spin 1.2s linear infinite;

        &:nth-child(2) {
          width: 80%;
          height: 80%;
          top: 10%;
          left: 10%;
          border-top-color: #52c41a;
          animation-duration: 1s;
          animation-direction: reverse;
        }

        &:nth-child(3) {
          width: 60%;
          height: 60%;
          top: 20%;
          left: 20%;
          border-top-color: #ffc107;
          animation-duration: 0.8s;
        }
      }
    }

    .pulse-ring {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 120%;
      height: 120%;
      border-radius: 50%;
      border: 2px solid rgba(67, 97, 238, 0.2);
      animation: pulse 2s ease-in-out infinite;
    }
  }

  .status-text {
    margin-bottom: 1.5rem;

    .status-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #333;
      margin-bottom: 0.5rem;
    }

    .status-instruction {
      color: #606266;
      font-size: 0.95rem;
      margin: 0;

      strong {
        color: #4361EE;
      }
    }
  }

  .elapsed-time {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    background-color: rgba(67, 97, 238, 0.05);
    padding: 0.75rem 1.5rem;
    border-radius: 30px;
    margin-bottom: 1.5rem;

    .time-label {
      font-size: 0.875rem;
      color: #606266;
    }

    .time-value {
      font-family: 'Courier New', monospace;
      font-size: 1.25rem;
      font-weight: 700;
      color: #4361EE;
    }
  }

  .timeout-warning {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background-color: rgba(255, 193, 7, 0.1);
    border-radius: 6px;
    color: #856404;
    font-size: 0.875rem;
    margin-bottom: 1.5rem;

    i {
      font-size: 1rem;
    }
  }

  .tips-section {
    text-align: left;
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 1rem 1.25rem;

    .tips-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 600;
      color: #333;
      margin-bottom: 0.75rem;
      font-size: 0.9rem;

      i {
        color: #ffc107;
      }
    }

    .tips-list {
      margin: 0;
      padding-left: 1.25rem;

      li {
        color: #606266;
        font-size: 0.85rem;
        margin-bottom: 0.5rem;
        line-height: 1.5;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.3;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 0.1;
  }
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
